import { useCallback, useState } from 'react';
import { friendlyDeleteError } from '../utils/deleteError';

interface Options<T> {
  /** What is being deleted, singular, for the error text: "task", "vendor". */
  record: string;
  /** Does the delete. Throw on failure; the reason is shown in the dialog. */
  perform: (target: T) => Promise<unknown>;
  /** Runs after a successful delete (refresh a list, show a toast, leave the page). */
  onDone?: (target: T) => void;
}

/**
 * State for a "delete forever" confirm dialog: which record, whether the call
 * is running, and the plain-language reason if it failed. On failure the
 * dialog stays open so the reason can be read; only success (or Cancel) closes it.
 * Spread the result into ConfirmDialog: open, onClose, onConfirm, error, loading.
 */
export function useDeleteConfirm<T>({ record, perform, onDone }: Options<T>) {
  const [target, setTarget] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const request = useCallback((item: T) => { setError(null); setTarget(item); }, []);
  const close = useCallback(() => { if (!loading) { setTarget(null); setError(null); } }, [loading]);

  const confirm = useCallback(async () => {
    if (target === null || loading) return;
    setLoading(true);
    setError(null);
    try {
      await perform(target);
    } catch (err) {
      setError(friendlyDeleteError(err, { record }));
      setLoading(false);
      return;
    }
    setLoading(false);
    const done = target;
    setTarget(null);
    onDone?.(done);
  }, [target, loading, perform, record, onDone]);

  return {
    target,
    request,
    dialogProps: { open: target !== null, onClose: close, onConfirm: confirm, error, loading },
  };
}
